import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { registerGoogleAnalyticsEvent } from './googleTagManagerDataLayer';

/**
 * Hook that submits page view data to Google Analytics after a specified delay.
 * 
 * This hook implements a debounce mechanism to ensure that page view events are only
 * submitted once per logical page view, even if the page title or content changes rapidly
 * (e.g., when a user is typing in a search field that updates the page title with each keystroke).
 * 
 * If new page data is received before the delay completes, the previous
 * submission is cancelled and a new one is scheduled.
 * The submission only happens if there are no API calls in progress.
 * 
 * Duplicate submissions are prevented by comparing the current payload with the last submitted one.
 * 
 * @param {Object} pageData - The page data object, meant to be generated by useGAnalyticsPageData
 * @param {string} pageData.page.title - The title of the page
 * @param {string} pageData.page.url - The URL of the page
 * @param {string} pageData.page.pageType - The type of the page
 */
const useGAnalyticsDelayedPageView = (pageData) => {
  const timerRef = useRef(null);
  const lastSubmittedPayloadRef = useRef(null);
  const apiCallsInProgress = useSelector(state => state.apiCallsInProgress);
  const ANALYTICS_SUBMISSION_DELAY_MS = 600; // Delay in milliseconds before submitting analytics

  const title = pageData?.page?.title;
  const url = pageData?.page?.url;
  const pageType = pageData?.page?.pageType;

  useEffect(() => {
    // Skip if required data is missing or API calls are in progress
    if (!title || !url || !pageType || apiCallsInProgress?.length) {
      return;
    }

    // Clear any existing timer to cancel previous pending submissions
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set a new timer to submit the page data after the specified delay
    timerRef.current = setTimeout(() => {
      submitAnalyticsIfDifferent(pageData);
      timerRef.current = null;
    }, ANALYTICS_SUBMISSION_DELAY_MS);

    // Cleanup function to clear the timer if component unmounts
    // or if the effect runs again before the timeout completes
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [title, url, pageType, apiCallsInProgress]); // Re-run effect when these dependencies change

  /**
   * Submits analytics data only if it differs from the last submission
   * @param {Object} data - The analytics data to submit
   */
  const submitAnalyticsIfDifferent = (data) => {
    try {
      const currentPayloadString = JSON.stringify(data);
      const lastPayloadString = lastSubmittedPayloadRef.current;
      
      // If payload is the same as last submitted, skip submission
      if (currentPayloadString === lastPayloadString) {
        return;
      }
      
      // Submit new payload and update reference
      registerGoogleAnalyticsEvent(data);
      lastSubmittedPayloadRef.current = currentPayloadString;
    } catch (error) {
      // Handle errors during comparison
      handleSubmissionError(data, error);
    }
  };

  /**
   * Handles errors during analytics submission
   * @param {Object} data - The analytics data that was being processed
   * @param {Error} error - The error that occurred
   */
  const handleSubmissionError = (data, error) => {
    console.error('Error comparing analytics payloads:', error);
    
    // Submit anyway to be safe
    registerGoogleAnalyticsEvent(data);
    
    // Try to update the last submitted payload reference
    try {
      lastSubmittedPayloadRef.current = JSON.stringify(data);
    } catch (e) {
      console.error('Failed to store last submitted payload:', e);
    }
  };
};

export default useGAnalyticsDelayedPageView;
